const TELEGRAM_WEB_APP_SCRIPT_ID = 'telegram-web-app-sdk';
const TELEGRAM_WEB_APP_SCRIPT_SRC = 'https://telegram.org/js/telegram-web-app.js';

function getTelegramRuntimeHints() {
  if (typeof window === 'undefined') return { userAgent: '', hasProxy: false };

  return {
    userAgent: window.navigator.userAgent.toLowerCase(),
    hasProxy: 'TelegramWebviewProxy' in window,
  };
}

export function looksLikeTelegramRuntime(): boolean {
  const { userAgent, hasProxy } = getTelegramRuntimeHints();
  return hasProxy || userAgent.includes('telegram');
}

export async function ensureTelegramWebAppSdk(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (window.Telegram?.WebApp) return true;
  if (!looksLikeTelegramRuntime()) return false;

  const existing = document.getElementById(TELEGRAM_WEB_APP_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    if (window.Telegram?.WebApp) return true;

    await new Promise<void>((resolve) => {
      const handleLoad = () => resolve();
      const handleError = () => resolve();
      existing.addEventListener('load', handleLoad, { once: true });
      existing.addEventListener('error', handleError, { once: true });
    });

    return Boolean(window.Telegram?.WebApp);
  }

  const script = document.createElement('script');
  script.id = TELEGRAM_WEB_APP_SCRIPT_ID;
  script.src = TELEGRAM_WEB_APP_SCRIPT_SRC;
  script.async = true;
  script.crossOrigin = 'anonymous';

  const loaded = new Promise<boolean>((resolve) => {
    script.addEventListener('load', () => resolve(Boolean(window.Telegram?.WebApp)), { once: true });
    script.addEventListener('error', () => resolve(false), { once: true });
  });

  document.head.appendChild(script);
  return loaded;
}

export function isTelegramEnv(): boolean {
  return typeof window !== 'undefined' && !!window.Telegram?.WebApp?.initData;
}

export function getInitData(): string {
  if (typeof window === 'undefined') return '';
  return window.Telegram?.WebApp?.initData ?? '';
}

export function hasTelegramWebAppBridge(): boolean {
  return typeof window !== 'undefined' && !!window.Telegram?.WebApp;
}

function sleep(delayMs: number) {
  return new Promise((resolve) => window.setTimeout(resolve, delayMs));
}

export async function waitForTelegramInitData(
  options: { attempts?: number; delayMs?: number } = {},
): Promise<string> {
  const attempts = options.attempts ?? 6;
  const delayMs = options.delayMs ?? 250;

  if (typeof window === 'undefined') return '';

  await ensureTelegramWebAppSdk();

  const tg = window.Telegram?.WebApp;
  tg?.ready?.();

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const initData = getInitData();
    if (initData) {
      return initData;
    }

    if (attempt < attempts - 1) {
      await sleep(delayMs);
    }
  }

  return '';
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        close: () => void;
        expand: () => void;
        ready: () => void;
        BackButton: {
          show: () => void;
          hide: () => void;
          onClick: (cb: () => void) => void;
          offClick: (cb: () => void) => void;
        };
        MainButton: {
          show: () => void;
          hide: () => void;
          setText: (text: string) => void;
          onClick: (cb: () => void) => void;
        };
        viewportHeight?: number;
        viewportStableHeight?: number;
        isExpanded?: boolean;
        platform?: string;
        version?: string;
        colorScheme: 'light' | 'dark';
        themeParams: {
          bg_color?: string;
          text_color?: string;
          hint_color?: string;
          link_color?: string;
          button_color?: string;
          button_text_color?: string;
        };
      };
    };
    TelegramWebviewProxy?: unknown;
  }
}
